import React, { useMemo, memo } from 'react';
import { Feature } from '../data/featureData';

interface SingleFeatureDisplayProps {
    feature: Feature;
    sectionIndex: number;
    isActiveSection: boolean;
    scrollY: number;
    viewportHeight: number;
    sectionStartVhValues: number[];
    isInteractionReady: boolean;
}

// Your original, correct animation ranges
const IMG_FADE_IN_START = 0.0;
const IMG_FADE_IN_END = 0.5;
const TEXT_APPEAR_AND_TRAVEL_START = 0.15;
const TEXT_SCROLL_OFF_END = 0.95;
const TOTAL_TEXT_TRAVEL_VH = -115;
const IMG_FADE_OUT_START = TEXT_SCROLL_OFF_END;
const IMG_FADE_OUT_END = 0.95;

const SingleFeatureDisplay: React.FC<SingleFeatureDisplayProps> = ({
    feature, sectionIndex, isActiveSection, scrollY, viewportHeight, sectionStartVhValues, isInteractionReady
}) => {
    const { responsiveImageWidth, responsiveAnnotationWidth, imageToDisplay } = useMemo(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        return {
            responsiveImageWidth: isMobile ? '90vw' : '880px',
            responsiveAnnotationWidth: isMobile ? '90vw' : '505px',
            imageToDisplay: isMobile ? feature.imageSrcMobile : feature.imageSrcDesktop,
        };
    }, [feature]);

    const progress = useMemo(() => {
        // This guard prevents calculations during the buggy grace period
        if (!isInteractionReady || viewportHeight <= 0) return 0;
        
        const startVh = sectionStartVhValues[sectionIndex] || 0;
        const sStartPx = (startVh / 100) * viewportHeight;
        const sHeightPx = (150 / 100) * viewportHeight;
        if (sHeightPx <= 0) return 0;

        const scrollInSection = scrollY - sStartPx;
        return Math.max(0, Math.min(1, scrollInSection / sHeightPx));
    }, [isInteractionReady, scrollY, viewportHeight, sectionIndex, sectionStartVhValues]);

    // Initialize variables
    let imageOpacity = 0;
    let textBoxOpacity = 0;
    let textBoxTranslateY = 0;
    let imageSpecificFilter = 'none';

    // This "grace period" check ensures your original logic only runs when ready
    if (isInteractionReady) {
        // 👇 YOUR ORIGINAL, SMOOTH ANIMATION LOGIC IS RESTORED HERE 👇
        if (progress >= IMG_FADE_IN_START && progress < IMG_FADE_IN_END) {
            imageOpacity = (progress - IMG_FADE_IN_START) / (IMG_FADE_IN_END - IMG_FADE_IN_START);
        } else if (progress >= IMG_FADE_IN_END && progress < IMG_FADE_OUT_START) {
            imageOpacity = 1.0;
        } else if (progress >= IMG_FADE_OUT_START && progress <= IMG_FADE_OUT_END) {
            imageOpacity = 1.0 - (progress - IMG_FADE_OUT_START) / (IMG_FADE_OUT_END - IMG_FADE_OUT_START);
            const fadeOutCompletion = (progress - IMG_FADE_OUT_START) / (IMG_FADE_OUT_END - IMG_FADE_OUT_START);
            const targetBrightness = 0.4;
            const targetSaturation = 0.2;
            const currentBrightness = 1.0 - (fadeOutCompletion * (1.0 - targetBrightness));
            const currentSaturation = 1.0 - (fadeOutCompletion * (1.0 - targetSaturation));
            imageSpecificFilter = `brightness(${currentBrightness.toFixed(2)}) saturate(${currentSaturation.toFixed(2)})`;
        } else {
            imageOpacity = 0.0;
        }

        if (progress >= TEXT_APPEAR_AND_TRAVEL_START && progress < TEXT_SCROLL_OFF_END) {
            textBoxOpacity = 1.0;
            const travelProgress = (progress - TEXT_APPEAR_AND_TRAVEL_START) / (TEXT_SCROLL_OFF_END - TEXT_APPEAR_AND_TRAVEL_START);
            textBoxTranslateY = TOTAL_TEXT_TRAVEL_VH * Math.max(0, Math.min(1, travelProgress));
        } else {
            textBoxOpacity = 0.0;
        }
    }
    
    // This new rule allows the fade-out animation to complete.
if (!isActiveSection && imageOpacity === 0) {
    return null;
}

    const containerStyle: React.CSSProperties = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        opacity: imageOpacity,
        pointerEvents: 'none',
        zIndex: 15,
    };

    const imageStyle: React.CSSProperties = {
        maxWidth: responsiveImageWidth, maxHeight: '80vh', width: 'auto', height: 'auto',
        objectFit: 'contain', filter: imageSpecificFilter,
    };

    const annotationBoxStyle: React.CSSProperties = {
    // Core Layout
    width: responsiveAnnotationWidth,
    maxWidth: '90%', // <-- From old style
    padding: '20px 24px',
    position: 'absolute', 
    top: '100vh', 
    left: '50%',
    zIndex: 12, // <-- From old style (was 20)

    // Desired Typography
    fontFamily: "'Inter', sans-serif", // <-- From old style
    fontWeight: 300,                  // <-- From old style
    fontSize: '20px',                   // <-- From old style
    lineHeight: '1.4',                  // <-- From old style
    color: '#1A1A1A',                     // <-- From old style
    textAlign: 'left',                  // <-- From old style
  
    // Background & Border
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '5px',
    border: '1px solid #b3b3b3',
    boxShadow: '0 0 10px rgba(132, 132, 132, 0.25)',

    // Animation & Interaction
    transform: `translateX(-50%) translateY(${textBoxTranslateY}vh)`,
    opacity: textBoxOpacity,
    pointerEvents: textBoxOpacity > 0 ? 'auto' : 'none',
    visibility: textBoxOpacity > 0 ? 'visible' : 'hidden', // <-- From old style
    willChange: 'transform, opacity',                       // <-- From old style
  };

    return (
        <div style={containerStyle}>
            <img src={imageToDisplay} alt={feature.imageAltText} style={imageStyle} />
            <div style={annotationBoxStyle}>{feature.annotationText}</div>
        </div>
    );
};

export default memo(SingleFeatureDisplay);